import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { Table, PageHeader, Card, Button } from "@cofano/cck";
import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";

import { Tabs, Tab, Menu, MenuItem, Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useFrontendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import useTabs from "components/hooks/useTabs";
import Helmet from "components/helmet/Helmet";
import usePopover from "components/hooks/usePopover";

import { Language, usePermission } from "imports/Utils";

import { StaffelTabs } from "./utils/StaffelUtils";

import { ChildPropsFromArray } from "types/commons";
import { GetAllStaffelsDocument, GetAllStaffelsQuery } from "__generated__/view/staffels/graphql/staffel.queries.generated";
import { DeleteStaffelDocument } from "__generated__/view/staffels/graphql/staffel.mutations.generated";

type StaffelFromQuery = ChildPropsFromArray<GetAllStaffelsQuery, "allStaffels">;

const Staffels = ({ defaultTab, onTabChange }: { defaultTab?: StaffelTabs, onTabChange: (tab: StaffelTabs) => void; }) => {

	const confirm = useConfirm();
	const navigate = useNavigate();

	const { selectedTab, selectTab } = useTabs({
		defaultSelected: defaultTab ?? StaffelTabs.GASOLINE,
	});

	const { popoverProps, onPopoverOpen } = usePopover({ trigger: "click" });
	const { isAdmin, isMasterdata, isReadOnly } = usePermission();

	const [ deleteStaffel ] = useMutation(DeleteStaffelDocument);

	const { data, loading, refetch } = useQuery(GetAllStaffelsDocument, {
		variables: {
			search: {
				type: selectedTab,
			},
		},
	});

	const staffels = data?.allStaffels ?? [];

	const { tableProps } = useFrontendTable({
		id: TableId.Staffels,
		content: staffels,
		paginationEnabled: false,
	});

	const onDeleteStaffel = (id: number) => {
		deleteStaffel({
			variables: {
				id: id,
			},
		}).then(() => refetch());
	};

	usePropertyChangeEffect(() => {
		refetch();
		onTabChange(selectedTab);
	}, [ selectedTab ]);

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("STAFFELS")}
					<Helmet title={intl.formatMessage({ id: "STAFFELS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && (
						<Button onClick={onPopoverOpen}>
							{Language("ADD_BUTTON")}
						</Button>
					)}

					<Menu {...popoverProps}>
						<MenuItem
							onClick={() => {
								navigate("/view/staffels/create/gasoline");
								onTabChange(StaffelTabs.GASOLINE);
							}}>
							{intl.formatMessage({ id: "GASOLINE_STAFFEL" })}
						</MenuItem>

						<MenuItem
							onClick={() => {
								navigate("/view/staffels/create/waterlevel");
								onTabChange(StaffelTabs.WATER_LEVEL);
							}}>
							{intl.formatMessage({ id: "WATERLEVEL" })}
						</MenuItem>

						<MenuItem
							onClick={() => {
								navigate("/view/staffels/create/feet");
								onTabChange(StaffelTabs.FEET);
							}}>
							{intl.formatMessage({ id: "FEET" })}
						</MenuItem>
					</Menu>
				</PageHeader.Actions>
			</PageHeader>

			<Box mb={2}>
				<Card disablePadding>
					<Tabs value={selectedTab} onChange={(event, newValue) => selectTab(newValue)}>
						<Tab label={Language("GASOIL")} value={StaffelTabs.GASOLINE} />
						<Tab label={Language("WATERLEVEL")} value={StaffelTabs.WATER_LEVEL} />
						<Tab label={Language("FEET")} value={StaffelTabs.FEET} />
					</Tabs>
				</Card>
			</Box>

			<Card disablePadding>
				<Table
					{...tableProps}
					disableFooter={true}
					contentLoading={loading}
					onRowDoubleClick={(row) => {
						const staffel = row as StaffelFromQuery;

						if (staffel.__typename === "GasolineStaffel") {
							navigate(`/view/staffels/${staffel.id}/details/gasoline`);
						} else if (staffel.__typename === "WaterLevelStaffel") {
							navigate(`/view/staffels/${staffel.id}/details/waterlevel`);
						} else {
							navigate(`/view/staffels/${staffel.id}/details/feet`);
						}
					}}
					columns={[ {
						id: "description",
						title: intl.formatMessage({ id: "DESCRIPTION" }),
						sortable: true,
						filterComponent: "TEXT",
					} ]}
					rowActions={(isAdmin || isMasterdata) && !isReadOnly ? [ {
						icon: <EditIcon />,
						onClick: (row: StaffelFromQuery) => {
							if (row.__typename === "GasolineStaffel") {
								navigate(`/view/staffels/${row.id}/edit/gasoline`);
							} else if (row.__typename === "WaterLevelStaffel") {
								navigate(`/view/staffels/${row.id}/edit/waterlevel`);
							} else {
								navigate(`/view/staffels/${row.id}/edit/feet`);
							}
						},
					}, {
						icon: <DeleteIcon />,
						onClick: (row) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								onDeleteStaffel(row.id);
							});
						},
					} ] : undefined} />
			</Card>
		</div>
	);
};

export default Staffels;
